// src/components/layout/AppLayout.tsx
// RBAC-BASED SIDEBAR WITH PERMISSION FILTERING + POS ACCESS FILTERING

import { useState, useMemo, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { BranchSelector } from '@/components/ui/BranchSelector';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import {
    LayoutDashboard, ShoppingCart, Package, Truck,
    Users, BarChart3, Settings, LogOut, ChevronLeft, ChevronRight,
    ClipboardList, Menu, FolderTree, Tag, Gift,
    CreditCard, FileText, Award, Layers,
    ArrowRightLeft, RefreshCw, History, TrendingUp,
    ChevronDown, FlaskConical, ChefHat, Utensils, Barcode,
    Store, RotateCcw, Star,
    Building2,
    Receipt,
    Factory,
    Sparkles,
    Warehouse,
    Upload,
    ShieldCheck,
    Calculator,
    MapPin,
    Server,
    Zap,
    Bell,
} from 'lucide-react';
import { clsx } from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

// ============================================================
// TYPES
// ============================================================

interface NavItem {
    name: string;
    href: string;
    icon: React.ElementType;
    requiredPermission?: string;
    badge?: string;
    badgeColor?: string;
}

interface NavSection {
    name: string;
    items: NavItem[];
    defaultOpen?: boolean;
    requiredPermission?: string;
}

// ============================================================
// REGULAR TENANT NAVIGATION (ORIGINAL PERMISSION CODES)
// ============================================================

const navigationSections: NavSection[] = [
    {
        name: 'MAIN',
        items: [
            { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
            { name: 'POS Terminal', href: '/pos', icon: ShoppingCart, requiredPermission: 'pos:access' },
        ],
        defaultOpen: true,
    },
    {
        name: 'BUSINESS SETUP',
        items: [
            { name: 'Businesses', href: '/businesses', icon: Building2, requiredPermission: 'businesses:view' },
            { name: 'Branches', href: '/branches', icon: Store, requiredPermission: 'settings:branches' },
            { name: 'Staff', href: '/staff', icon: Users, requiredPermission: 'users:view' },
            { name: 'Staff Performance', href: '/staff/performance', icon: TrendingUp, requiredPermission: 'users:view' },
            { name: 'Bank Accounts', href: '/bank-accounts', icon: CreditCard, requiredPermission: 'banking:view' },
            { name: 'Expenses', href: '/expenses', icon: Receipt, requiredPermission: 'expenses:view' },
        ],
        defaultOpen: true,
    },
    {
        name: 'CATALOG',
        requiredPermission: 'products:view',
        items: [
            { name: 'Categories', href: '/categories', icon: FolderTree, requiredPermission: 'categories:view' },
            { name: 'Products', href: '/products', icon: Package, requiredPermission: 'products:view' },
            { name: 'Recipes', href: '/recipes', icon: ChefHat, requiredPermission: 'bom:view' },
            { name: 'Print Barcodes', href: '/products/barcodes/print', icon: Barcode, requiredPermission: 'products:view' },
        ],
        defaultOpen: true,
    },
    {
        name: 'PRODUCTION',
        requiredPermission: 'bom:view',
        items: [
            { name: 'BOM Recipes', href: '/bom', icon: FlaskConical, requiredPermission: 'bom:view' },
            { name: 'Production Floors', href: '/bom/production-floors', icon: Factory, requiredPermission: 'bom:view' },
            { name: 'Start Production', href: '/bom/production', icon: ChefHat, requiredPermission: 'products:create' },
            { name: 'Menu POS', href: '/bom-pos', icon: Utensils, requiredPermission: 'pos:access' },
            { name: 'Kitchen Display', href: '/kitchen', icon: ChefHat, requiredPermission: 'kitchen:view' },
        ],
        defaultOpen: false,
    },
    {
        name: 'INVENTORY & STOCK',
        requiredPermission: 'inventory:view',
        items: [
            { name: 'Dashboard', href: '/inventory', icon: Warehouse, requiredPermission: 'inventory:view' },
            { name: 'Stock Levels', href: '/inventory/levels', icon: Layers, requiredPermission: 'stock:view' },
            { name: 'Review & Confirm', href: '/inventory/review', icon: ShieldCheck, requiredPermission: 'stock:edit' },
            { name: 'Stock Count', href: '/inventory/counts', icon: ClipboardList, requiredPermission: 'inventory:view' },
            { name: 'Adjust Stock', href: '/inventory/adjust', icon: RefreshCw, requiredPermission: 'stock:edit' },
            { name: 'Transfers', href: '/inventory/transfers', icon: ArrowRightLeft, requiredPermission: 'transfers:view' },
            { name: 'Ledger', href: '/inventory/ledger', icon: History, requiredPermission: 'transactions:view' },
            { name: 'Reorder', href: '/inventory/reorder', icon: ShoppingCart, requiredPermission: 'inventory:view' },
            { name: 'Analytics', href: '/inventory/analytics', icon: BarChart3, requiredPermission: 'inventory:view' },
            { name: 'Valuation', href: '/inventory/valuation', icon: Calculator, requiredPermission: 'inventory:view' },
            { name: 'Locations', href: '/inventory/locations', icon: MapPin, requiredPermission: 'stock:view' },
            { name: 'Batches', href: '/inventory/batches', icon: Package, requiredPermission: 'inventory:view' },
            { name: 'ABC Classification', href: '/inventory/classification', icon: BarChart3, requiredPermission: 'inventory:view' },
            { name: 'Notifications', href: '/inventory/notifications', icon: Bell, requiredPermission: 'inventory:view' },
            { name: 'Setup', href: '/inventory/setup', icon: Sparkles, requiredPermission: 'inventory:view' },
            { name: 'Settings', href: '/inventory/settings', icon: Settings, requiredPermission: 'settings:view' },
        ],
        defaultOpen: true,
    },
    {
        name: 'PROCUREMENT',
        requiredPermission: 'procurement:view',
        items: [
            { name: 'Dashboard', href: '/procurement', icon: Truck, requiredPermission: 'procurement:view' },
            { name: 'Requisitions', href: '/procurement/requisitions', icon: ClipboardList, requiredPermission: 'procurement:view' },
            { name: 'Purchase Orders', href: '/procurement/orders', icon: FileText, requiredPermission: 'procurement:view' },
            { name: 'Receive Goods (GRN)', href: '/procurement/grns', icon: Package, requiredPermission: 'procurement:view' },
            { name: 'Return Items', href: '/procurement/returns', icon: RotateCcw, requiredPermission: 'procurement:view' },
            { name: 'Suppliers', href: '/vendors', icon: Store, requiredPermission: 'procurement:view' },
        ],
        defaultOpen: false,
    },
    {
        name: 'SALES',
        requiredPermission: 'sales:view',
        items: [
            { name: 'Sales', href: '/sales', icon: BarChart3, requiredPermission: 'sales:view' },
            { name: 'Customers', href: '/customers', icon: Users, requiredPermission: 'customers:view' },
            { name: 'Loyalty', href: '/loyalty', icon: Award, requiredPermission: 'customers:view' },
        ],
        defaultOpen: false,
    },
    {
        name: 'FINANCE',
        requiredPermission: 'finance:view',
        items: [
            { name: 'Invoices', href: '/invoices', icon: FileText, requiredPermission: 'invoices:view' },
            { name: 'Banking', href: '/banking', icon: CreditCard, requiredPermission: 'banking:view' },
        ],
        defaultOpen: false,
    },
    {
        name: 'OTHER',
        items: [
            { name: 'Reports', href: '/reports', icon: TrendingUp, requiredPermission: 'reports:view' },
            { name: 'Hampers', href: '/hampers', icon: Gift, requiredPermission: 'hampers:view' },
            { name: 'Clearance', href: '/clearance', icon: Tag, requiredPermission: 'clearance:view' },
            { name: 'Security Verify', href: '/security/verify', icon: Star, requiredPermission: 'security:verify' },
        ],
        defaultOpen: false,
    },
    {
        name: 'SYSTEM',
        requiredPermission: 'settings:view',
        items: [
            { name: 'Settings', href: '/settings', icon: Settings, requiredPermission: 'settings:view' },
        ],
        defaultOpen: true,
    },
];

// ============================================================
// SUPER ADMIN NAVIGATION
// ============================================================

const superAdminNavigation: NavSection[] = [
    {
        name: 'PLATFORM',
        items: [
            { name: 'Dashboard', href: '/super-admin', icon: Zap },
            { name: 'Tenants', href: '/super-admin/tenants', icon: Building2 },
            { name: 'Subscriptions', href: '/super-admin/subscriptions', icon: CreditCard },
            { name: 'Plans & Features', href: '/super-admin/plans', icon: Layers },
        ],
        defaultOpen: true,
    },
    {
        name: 'MONITORING',
        items: [
            { name: 'Audit Logs', href: '/super-admin/audit-logs', icon: FileText },
            { name: 'Error Logs', href: '/super-admin/error-logs', icon: Server },
            { name: 'System Health', href: '/super-admin', icon: Server },
        ],
        defaultOpen: true,
    },
    {
        name: 'ACCOUNT',
        items: [
            { name: 'Sign Out', href: '#logout', icon: LogOut },
        ],
        defaultOpen: true,
    },
];

// ============================================================
// COMPONENT
// ============================================================

export function AppLayout() {
    const { user, logout, branches, tenant } = useAuthStore();
    const { sidebarCollapsed, toggleSidebar, setSidebarCollapsed } = useUIStore();
    const navigate = useNavigate();
    const location = useLocation();

    // ✅ Get Platform Admin and Super Admin flags from JWT directly
    const { isSuperAdminFromJwt, isPlatformAdminFromJwt } = (() => {
        try {
            const token = localStorage.getItem('accessToken');
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                return {
                    isSuperAdminFromJwt: payload.isSuperAdmin || payload.isPlatformAdmin || false,
                    isPlatformAdminFromJwt: payload.isPlatformAdmin || false,
                };
            }
        } catch { }
        return { isSuperAdminFromJwt: false, isPlatformAdminFromJwt: false };
    })();

    const impersonatedTenantName = localStorage.getItem('impersonatedTenantName');
    const originalPlatformToken = localStorage.getItem('originalPlatformAccessToken');
    const isImpersonating = !!originalPlatformToken && !!impersonatedTenantName;

    const handleExitImpersonation = () => {
        const origAccess = localStorage.getItem('originalPlatformAccessToken');
        const origRefresh = localStorage.getItem('originalPlatformRefreshToken');
        if (origAccess) localStorage.setItem('accessToken', origAccess);
        if (origRefresh) localStorage.setItem('refreshToken', origRefresh);
        localStorage.removeItem('originalPlatformAccessToken');
        localStorage.removeItem('originalPlatformRefreshToken');
        localStorage.removeItem('impersonatedTenantName');
        window.location.href = '/super-admin/tenants';
    };

    const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {};
        navigationSections.forEach(section => {
            initial[section.name] = section.defaultOpen ?? false;
        });
        superAdminNavigation.forEach(section => {
            initial[section.name] = section.defaultOpen ?? false;
        });
        return initial;
    });

    const { data: permissionData, isLoading: permissionsLoading } = useQuery({
        queryKey: ['user-permissions', user?.id, user?.tenantId],
        queryFn: async () => {
            if (!user?.tenantId || !user?.id) {
                return { permissions: [], isSuperAdmin: false, isSystemSuperAdmin: false, roles: [] };
            }

            try {
                const { data } = await apiClient.get('/auth/me/permissions');
                const responseData = data?.data || data;

                const isSuperAdmin = responseData?.roles?.some(
                    (r: any) => r.name === 'Super Admin'
                ) || isSuperAdminFromJwt;

                const SYSTEM_TENANT_ID = import.meta.env.VITE_SYSTEM_TENANT_ID || 'system';
                const isSystemTenant =
                    tenant?.slug === 'system' ||
                    user?.email?.toLowerCase() === 'inyangpsalms@gmail.com' ||
                    user.tenantId === SYSTEM_TENANT_ID ||
                    user.tenantId === '49a95754-da93-49ff-91a5-cd12dc082d28' ||
                    user.tenantId === '9ec96208-5114-4cec-94f2-7797260c9ba2' ||
                    user.tenantId === 'de89f22b-497e-4f6e-b1d6-0b11e922eb72' ||
                    tenant?.name?.toLowerCase().includes('system platform') ||
                    tenant?.name?.toLowerCase().includes('etipos system') ||
                    localStorage.getItem('tenantSlug') === 'system';

                const isSystemSuperAdmin =
                    responseData?.isPlatformAdmin === true ||
                    isPlatformAdminFromJwt ||
                    (isSuperAdmin && isSystemTenant);

                const permissions = responseData?.permissions || [];
                const roles = responseData?.roles?.map((r: any) => r.name) || [];

                return { permissions, isSuperAdmin, isSystemSuperAdmin, roles };
            } catch (error) {
                const SYSTEM_TENANT_ID = import.meta.env.VITE_SYSTEM_TENANT_ID || 'system';
                const isSystemTenant =
                    tenant?.slug === 'system' ||
                    user.tenantId === SYSTEM_TENANT_ID ||
                    localStorage.getItem('tenantSlug') === 'system';

                return {
                    permissions: [],
                    isSuperAdmin: isSuperAdminFromJwt,
                    isSystemSuperAdmin: isPlatformAdminFromJwt || (isSuperAdminFromJwt && isSystemTenant),
                    roles: []
                };
            }
        },
        enabled: !!user?.tenantId && !!user?.id,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const SYSTEM_TENANT_ID = import.meta.env.VITE_SYSTEM_TENANT_ID || 'system';
    const isSystemOwner =
        permissionData?.isSystemSuperAdmin ||
        isPlatformAdminFromJwt ||
        (user as any)?.isPlatformAdmin ||
        (isSuperAdminFromJwt && (
            tenant?.slug === 'system' ||
            user?.email?.toLowerCase() === 'inyangpsalms@gmail.com' ||
            user?.tenantId === SYSTEM_TENANT_ID ||
            user?.tenantId === '49a95754-da93-49ff-91a5-cd12dc082d28' ||
            localStorage.getItem('tenantSlug') === 'system'
        )) ||
        false;
    const activeNavigation = isSystemOwner ? superAdminNavigation : navigationSections;

    const hasPermission = useCallback((permissionCode?: string): boolean => {
        if (!permissionCode) return true;
        if (isPlatformAdminFromJwt || (isSuperAdminFromJwt && isSystemOwner)) return true;
        if (!permissionData) return false;
        if (permissionData.isSystemSuperAdmin) return true;
        if (permissionCode === 'super:admin') return permissionData.isSystemSuperAdmin;
        if (permissionData.isSuperAdmin) return true;
        if (permissionData.permissions.includes('*') ||
            permissionData.permissions.includes('admin:all')) {
            return true;
        }

        // Direct match
        if (permissionData.permissions.includes(permissionCode)) return true;

        // Aliases for business / businesses
        if (permissionCode === 'businesses:view' && permissionData.permissions.includes('business:view')) return true;
        if (permissionCode === 'business:view' && permissionData.permissions.includes('businesses:view')) return true;

        // Aliases for branches / settings:branches
        if ((permissionCode === 'settings:branches' || permissionCode === 'branches:view') &&
            (permissionData.permissions.includes('settings:branches') ||
             permissionData.permissions.includes('branches:view') ||
             permissionData.permissions.includes('settings:view'))) {
            return true;
        }

        return false;
    }, [permissionData, isSuperAdminFromJwt]);

    const filteredSections = useMemo(() => {
        if (!permissionData && !isSuperAdminFromJwt) {
            return activeNavigation
                .map(section => ({
                    ...section,
                    items: section.items.filter(item => !item.requiredPermission),
                }))
                .filter(section => section.items.length > 0);
        }

        return activeNavigation
            .map(section => {
                const filteredItems = section.items.filter(item =>
                    hasPermission(item.requiredPermission)
                );
                return { ...section, items: filteredItems };
            })
            .filter(section => {
                if (section.items.length === 0) return false;
                if (!section.requiredPermission) return true;
                return hasPermission(section.requiredPermission);
            });
    }, [permissionData, hasPermission, activeNavigation, isSuperAdminFromJwt]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const toggleSection = (sectionName: string) => {
        setOpenSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
    };

    const isRouteActive = (href: string) => {
        if (['/dashboard', '/pos', '/tutorials', '/kitchen'].includes(href)) {
            return location.pathname === href;
        }
        const baseHref = href.split('?')[0];
        if (location.pathname === baseHref) return true;
        if (location.pathname.startsWith(baseHref + '/')) return true;
        return false;
    };

    return (
        <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors">
            <AnimatePresence>
                {!sidebarCollapsed && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-20 lg:hidden backdrop-blur-sm"
                        onClick={() => setSidebarCollapsed(true)}
                    />
                )}
            </AnimatePresence>

            <aside className={clsx(
                'fixed left-0 top-0 h-full bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transition-all duration-300 z-30 flex flex-col shadow-xl shadow-slate-200/50 dark:shadow-none',
                sidebarCollapsed ? '-translate-x-full lg:translate-x-0 lg:w-[76px]' : 'translate-x-0 w-[280px]'
            )}>
                <div className="flex items-center justify-between h-16 px-5 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
                    {!sidebarCollapsed ? (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex items-center gap-3 cursor-pointer"
                            onClick={() => navigate(isSystemOwner ? '/super-admin' : '/dashboard')}>
                            <div className={clsx(
                                'w-9 h-9 rounded-xl flex items-center justify-center shadow-lg',
                                isSystemOwner
                                    ? 'bg-gradient-to-br from-violet-500 to-purple-600 shadow-violet-500/20'
                                    : 'bg-gradient-to-br from-teal-500 to-teal-700 shadow-teal-500/20'
                            )}>
                                <span className="text-white font-bold text-lg">E</span>
                            </div>
                            <h1 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                                {isSystemOwner ? 'EtiPOS Admin' : 'EtiPOS'}
                            </h1>
                        </motion.div>
                    ) : (
                        <motion.div
                            className={clsx(
                                'w-9 h-9 rounded-xl flex items-center justify-center shadow-lg mx-auto cursor-pointer',
                                isSystemOwner
                                    ? 'bg-gradient-to-br from-violet-500 to-purple-600 shadow-violet-500/20'
                                    : 'bg-gradient-to-br from-teal-500 to-teal-700 shadow-teal-500/20'
                            )}
                            onClick={() => navigate(isSystemOwner ? '/super-admin' : '/dashboard')}>
                            <span className="text-white font-bold text-lg">E</span>
                        </motion.div>
                    )}
                    <button
                        onClick={toggleSidebar}
                        className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 dark:text-slate-500 hidden lg:block transition-colors"
                        type="button">
                        {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                    </button>
                </div>

                <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto scrollbar-thin">
                    {permissionsLoading && !isSuperAdminFromJwt && (
                        <div className="flex items-center justify-center py-8">
                            <div className="w-5 h-5 border-2 border-teal-200 border-t-teal-600 rounded-full animate-spin" />
                        </div>
                    )}

                    {filteredSections.map((section) => {
                        const sectionActive = section.items.some(item => isRouteActive(item.href));
                        const isOpen = openSections[section.name];

                        return (
                            <div key={section.name}>
                                {!sidebarCollapsed && (
                                    <button
                                        onClick={() => toggleSection(section.name)}
                                        className={clsx(
                                            'flex items-center justify-between w-full px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors',
                                            sectionActive
                                                ? isSystemOwner ? 'text-violet-600 dark:text-violet-400' : 'text-teal-600 dark:text-teal-400'
                                                : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
                                        )}
                                        type="button">
                                        <span>{section.name}</span>
                                        <motion.div
                                            animate={{ rotate: isOpen ? 180 : 0 }}
                                            transition={{ duration: 0.2 }}>
                                            <ChevronDown size={12} />
                                        </motion.div>
                                    </button>
                                )}

                                <AnimatePresence initial={false}>
                                    {(sidebarCollapsed || isOpen) && (
                                        <motion.div
                                            initial={!sidebarCollapsed ? { height: 0, opacity: 0 } : false}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={!sidebarCollapsed ? { height: 0, opacity: 0 } : undefined}
                                            transition={{ duration: 0.2 }}
                                            className="space-y-0.5 overflow-hidden mt-1">
                                            {section.items.map((item) => {
                                                const isActive = isRouteActive(item.href);
                                                const isSignOut = item.href === '#logout';
                                                const activeBg = isSystemOwner
                                                    ? 'bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300'
                                                    : 'bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300';
                                                const activeIndicator = isSystemOwner ? 'bg-violet-500' : 'bg-teal-500';

                                                return (
                                                    <a
                                                        key={item.name}
                                                        href={isSignOut ? '#' : item.href}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            if (isSignOut) {
                                                                handleLogout();
                                                                return;
                                                            }
                                                            navigate(item.href);
                                                            if (window.innerWidth < 1024) setSidebarCollapsed(true);
                                                        }}
                                                        className={clsx(
                                                            'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 relative group',
                                                            isActive
                                                                ? `${activeBg} shadow-sm`
                                                                : isSignOut
                                                                    ? 'text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:text-rose-600'
                                                                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white'
                                                        )}>
                                                        <item.icon
                                                            size={19}
                                                            className={clsx(
                                                                'flex-shrink-0 transition-colors',
                                                                isActive
                                                                    ? isSystemOwner ? 'text-violet-600 dark:text-violet-400' : 'text-teal-600 dark:text-teal-400'
                                                                    : isSignOut
                                                                        ? 'text-rose-400'
                                                                        : 'text-slate-400 dark:text-slate-500 group-hover:text-slate-500 dark:group-hover:text-slate-300'
                                                            )}
                                                        />
                                                        {!sidebarCollapsed && (
                                                            <span className="truncate">{item.name}</span>
                                                        )}
                                                        {isActive && !isSignOut && (
                                                            <motion.div
                                                                layoutId="activeNavIndicator"
                                                                className={clsx(
                                                                    'absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full',
                                                                    activeIndicator
                                                                )}
                                                                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                                            />
                                                        )}
                                                        {sidebarCollapsed && (
                                                            <div className="absolute left-full ml-3 px-2.5 py-1.5 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 shadow-lg">
                                                                {item.name}
                                                            </div>
                                                        )}
                                                    </a>
                                                );
                                            })}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}

                    {!permissionsLoading && filteredSections.length === 0 && (
                        <div className="text-center py-8 px-3">
                            <p className="text-xs text-slate-400 dark:text-slate-500">No menu items available</p>
                            <p className="text-[10px] text-slate-300 dark:text-slate-600 mt-1">Contact your administrator</p>
                        </div>
                    )}
                </nav>

                <div className="flex-shrink-0 border-t border-slate-100 dark:border-slate-800 p-3 space-y-3">
                    <ThemeToggle compact={sidebarCollapsed} className="w-full" />

                    <div className={clsx(
                        'flex items-center gap-3 px-1',
                        sidebarCollapsed && 'justify-center flex-col'
                    )}>
                        <div className={clsx(
                            'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg',
                            isSystemOwner
                                ? 'bg-gradient-to-br from-violet-500 to-purple-600 shadow-violet-500/20'
                                : 'bg-gradient-to-br from-teal-500 to-teal-700 shadow-teal-500/20'
                        )}>
                            <span className="text-sm font-bold text-white">
                                {user?.firstName?.[0]}{user?.lastName?.[0]}
                            </span>
                        </div>
                        {!sidebarCollapsed && (
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-slate-900 dark:text-white truncate">
                                    {user?.firstName} {user?.lastName}
                                </p>
                                <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{user?.email}</p>
                                {isSystemOwner && (
                                    <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 rounded-md text-[10px] font-bold">
                                        <Star size={10} /> System Owner
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {!sidebarCollapsed && (
                        <motion.button
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleLogout}
                            className="flex items-center gap-2.5 text-sm text-slate-500 hover:text-rose-500 dark:text-slate-400 dark:hover:text-rose-400 w-full px-3 py-2.5 rounded-xl hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-all font-medium"
                            type="button">
                            <LogOut size={16} />
                            <span>Sign out</span>
                        </motion.button>
                    )}
                </div>
            </aside>

            <main className={clsx(
                'flex-1 transition-all duration-300 flex flex-col min-h-screen',
                sidebarCollapsed ? 'lg:ml-[76px]' : 'lg:ml-[280px]'
            )}>
                {isImpersonating && (
                    <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white px-4 py-2.5 text-xs font-semibold flex items-center justify-between shadow-lg sticky top-0 z-40 backdrop-blur-md">
                        <div className="flex items-center gap-2.5">
                            <span className="p-1 rounded-md bg-white/20 text-white flex-shrink-0">🛡️</span>
                            <span>
                                <strong>Support Mode Active:</strong> You are currently viewing as organization <span className="underline decoration-white/60 font-black">{impersonatedTenantName}</span>. Any changes modify live operational data.
                            </span>
                        </div>
                        <button
                            onClick={handleExitImpersonation}
                            className="px-3.5 py-1.5 bg-white text-slate-900 rounded-lg font-bold hover:bg-slate-100 transition shadow-sm text-xs flex-shrink-0 flex items-center gap-1.5 cursor-pointer">
                            <span>Exit Impersonation</span>
                            <span className="text-[10px] text-slate-500 font-normal">→ Platform Owner</span>
                        </button>
                    </div>
                )}

                {/* Sticky Top Header Bar for Desktop & Mobile */}
                <header className="sticky top-0 z-20 h-16 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 px-4 lg:px-6 flex items-center justify-between transition-colors shadow-xs">
                    {/* Left side */}
                    <div className="flex items-center gap-3">
                        {/* Mobile hamburger menu */}
                        <button
                            onClick={toggleSidebar}
                            className="lg:hidden p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition-colors"
                            type="button"
                            aria-label="Toggle navigation menu">
                            <Menu size={20} />
                        </button>

                        {/* Mobile Logo */}
                        <div
                            className="lg:hidden flex items-center gap-2 cursor-pointer"
                            onClick={() => navigate(isSystemOwner ? '/super-admin' : '/dashboard')}>
                            <div className={clsx(
                                'w-7 h-7 rounded-lg flex items-center justify-center',
                                isSystemOwner
                                    ? 'bg-gradient-to-br from-violet-500 to-purple-600'
                                    : 'bg-gradient-to-br from-teal-500 to-teal-700'
                            )}>
                                <span className="text-white font-bold text-sm">E</span>
                            </div>
                            <h1 className={clsx(
                                'text-base font-bold',
                                isSystemOwner ? 'text-violet-600 dark:text-violet-400' : 'text-teal-600 dark:text-teal-400'
                            )}>
                                {isSystemOwner ? 'EtiPOS Admin' : 'EtiPOS'}
                            </h1>
                        </div>

                        {/* Desktop Store / Tenant Context */}
                        <div className="hidden lg:flex items-center gap-2.5">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                {isSystemOwner ? 'Platform Administration' : 'Branch Operations'}
                            </span>
                        </div>
                    </div>

                    {/* Right side: Branch Selector + Controls */}
                    <div className="flex items-center gap-2.5 sm:gap-3">
                        {!isSystemOwner && (
                            <div className="flex items-center">
                                <BranchSelector />
                            </div>
                        )}
                        <ThemeToggle compact />
                    </div>
                </header>

                <motion.div
                    className="p-4 lg:p-6"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}>
                    <Outlet />
                </motion.div>
            </main>
        </div>
    );
}